import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getProduct,
  getProductSerials,
  getProductSerialsDrafts,
  postNewComment,
} from "../../../components/products/functions";
import { deleteSerial } from "../../../components/serials/functions";
import ToastContext from "../../../context/toastProvider";
import {
  cashnator,
  cpfMask,
  formatarData,
  formatarDataCompleta,
  phoneMask,
} from "../../../components/utils/functions";
import { statusObject } from "../../../components/utils/allProductStatus";
import { getGradingTypeLabel } from "../../../components/utils/gradingTypes";
import {
  getVisibleGradeFields,
  renderApplicableGradeValue,
} from "../../../components/utils/serialGradeFields";
import MobileSection from "../../ui/MobileSection";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileConfirm from "../../ui/MobileConfirm";
import {
  MobileProductStatusSheet,
  MobileProductEditSheet,
  MobileSerialCreateSheet,
  MobileSerialEditSheet,
} from "../products/MobileProductDetailSheets";

const actionBtnClass =
  "inline-flex min-h-[36px] items-center rounded-lg border border-primary/50 px-3 text-xs font-medium text-laranjinha/80 hover:bg-monao";
const primaryBtnClass =
  "inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-green-700 px-4 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-50";

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <p className="text-sm text-laranjinha/80">
      <span className="text-laranjinha/60">{label}: </span>
      <span className="font-medium text-laranjinha">{value}</span>
    </p>
  );
}

export default function MobileRegisterSerials({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);

  const products = location.state?.products;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serials, setSerials] = useState([]);
  const [serialsLoading, setSerialsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [serialCreateOpen, setSerialCreateOpen] = useState(false);
  const [serialEditOpen, setSerialEditOpen] = useState(false);
  const [editingSerial, setEditingSerial] = useState(null);
  const [deleteSerialTarget, setDeleteSerialTarget] = useState(null);

  const loadSerials = useCallback(async (productId) => {
    if (!productId || !user?.token) return;
    setSerialsLoading(true);
    const serialsData = await getProductSerials(user.token, productId);
    const draftsData = await getProductSerialsDrafts(user.token, productId);
    if (serialsData?.type === "success") {
      const drafts =
        draftsData?.type === "success"
          ? (draftsData.data ?? []).filter((d) => d?.isDraft)
          : [];
      setSerials([...(serialsData.data ?? []), ...drafts]);
    }
    setSerialsLoading(false);
  }, [user?.token]);

  const loadProduct = useCallback(async () => {
    if (!products?.length) return;
    setLoading(true);
    const data = await getProduct(user?.token, products[currentIndex]);
    if (data?.type === "success") {
      setProduct(data.data);
      await loadSerials(data.data?._id);
    } else {
      setProduct(null);
      setSerials([]);
    }
    setLoading(false);
  }, [user?.token, products, currentIndex, loadSerials]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const refreshProduct = async () => {
    await loadProduct();
  };

  const submitComment = async () => {
    if (!newComment || !product?._id) return;
    setCommentBusy(true);
    const data = await postNewComment(user?.token, product._id, newComment);
    setCommentBusy(false);
    if (data?.type === "success") {
      goodToast("Comentário adicionado");
      setNewComment("");
      refreshProduct();
      return;
    }
    badToast(data?.message ?? "Erro ao adicionar comentário");
  };

  const confirmDeleteSerial = async () => {
    if (!deleteSerialTarget) return;
    const data = await deleteSerial(
      user?.token,
      deleteSerialTarget._id,
      deleteSerialTarget.isDraft
    );
    setDeleteSerialTarget(null);
    if (data?.type !== "success") {
      badToast("Erro ao excluir serial");
      return;
    }
    goodToast("Serial excluído!");
    loadSerials(product?._id);
  };

  const goToTagGenerator = () => {
    if (!product?._id) return;
    navigate("/manafix/serials/tags", {
      state: { origin: "products", products: [product._id] },
    });
  };

  if (!products?.length) {
    return (
      <div className="px-4 py-6">
        <h2 className="text-center text-lg font-semibold text-laranjinha">
          Registro de seriais
        </h2>
        <p className="mt-4 text-center text-sm text-laranjinha/70">Nenhum pedido selecionado</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-8">
      <div className="border-b border-primary/30 bg-mgs-bg px-4 py-3 text-center">
        <h2 className="text-lg font-semibold text-laranjinha">Registro de seriais</h2>
        <p className="mt-1 text-sm font-medium text-laranjinha/80">
          Pedido {currentIndex + 1}/{products.length}
        </p>
      </div>

      {loading ? (
        <div className="px-4 py-4" aria-label="Carregando pedido" role="status">
          <MobileSkeleton lines={6} />
        </div>
      ) : product ? (
        <div className="space-y-4 px-4 py-4">
          <MobileSection title="Usuário">
            <DetailRow label="Nome" value={product.user?.name} />
            <DetailRow label="E-mail" value={product.user?.email} />
            <DetailRow label="Telefone" value={phoneMask(product.user?.phone)} />
            <DetailRow label="CPF" value={cpfMask(product.user?.documentNumber)} />
            <DetailRow label="Nascimento" value={formatarData(product.user?.birth)} />
          </MobileSection>

          <MobileSection title="Pedido">
            <DetailRow label="ID" value={product._id} />
            <DetailRow label="Status" value={statusObject[product.status]} />
            <DetailRow label="Tipo" value={getGradingTypeLabel(product.gradingType)} />
            <DetailRow label="Autógrafo" value={product.autographEvaluation ? "Sim" : "Não"} />
            <DetailRow label="Nº de itens" value={product.numberOfCards} />
            <DetailRow label="Preço final" value={cashnator(product.finalPrice)} />
            <DetailRow label="Criado em" value={formatarData(product.createdAt)} />
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setStatusSheetOpen(true)}
                className={actionBtnClass}
              >
                Alterar status
              </button>
              <button
                type="button"
                onClick={() => setEditSheetOpen(true)}
                className={actionBtnClass}
              >
                Editar pedido
              </button>
            </div>
          </MobileSection>

          <MobileSection title="Comentários">
            {product.comments?.length ? (
              <ul className="mb-4 space-y-2">
                {product.comments.map((comment, index) => (
                  <li key={index} className="rounded-lg border border-primary/30 px-3 py-2 text-sm">
                    <p className="text-laranjinha">{comment.text}</p>
                    <p className="mt-1 text-xs text-laranjinha/60">
                      {formatarDataCompleta(comment.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-4 text-sm text-laranjinha/60">Nenhum comentário</p>
            )}
            <label className="block text-sm font-medium text-laranjinha/80">
              Novo comentário
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                maxLength={300}
                rows={3}
                className="mt-1 w-full rounded-lg border border-primary/50 px-3 py-2 text-base text-laranjinha"
              />
            </label>
            <button
              type="button"
              disabled={!newComment || commentBusy}
              onClick={submitComment}
              className={`mt-2 ${primaryBtnClass}`}
            >
              {commentBusy ? "Enviando…" : "Adicionar comentário"}
            </button>
          </MobileSection>

          <MobileSection title="Seriais do pedido">
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={serials.length < product.numberOfCards}
                onClick={goToTagGenerator}
                className="inline-flex min-h-[36px] items-center rounded-lg bg-primary px-3 text-xs font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
              >
                Gerar etiquetas
              </button>
            </div>

            {serialsLoading ? (
              <MobileSkeleton lines={3} />
            ) : serials.length === 0 ? (
              <MobileEmpty
                title="Sem seriais"
                message="Nenhum serial cadastrado para este pedido."
              />
            ) : (
              <ul className="space-y-2">
                {serials.map((serial) => {
                  const visibleGrades = getVisibleGradeFields(serial, product);
                  return (
                    <li
                      key={serial._id}
                      className="rounded-lg border border-primary/30 px-3 py-2 text-sm text-laranjinha"
                    >
                      <p className="font-medium">
                        {serial.serialNumber ?? serial._id}
                        {serial.isDraft ? (
                          <span className="ml-2 text-xs text-primary">(rascunho)</span>
                        ) : null}
                      </p>
                      {serial.gradableModel?.name ? (
                        <p className="text-xs text-laranjinha/60">
                          {serial.gradableModel.name} {serial.gradableModel.numbering}
                        </p>
                      ) : null}
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-laranjinha/70">
                        {visibleGrades.generalScore ? (
                          <span>
                            {visibleGrades.generalScore.label}:{" "}
                            {renderApplicableGradeValue(visibleGrades.generalScore.value)}
                          </span>
                        ) : null}
                        {visibleGrades.subgrades.map((field) => (
                          <span key={field.key}>
                            {field.label}: {renderApplicableGradeValue(field.value)}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSerial(serial);
                            setSerialEditOpen(true);
                          }}
                          className={actionBtnClass}
                        >
                          Editar notas
                        </button>
                        {user?.roles?.includes("admin") ? (
                          <button
                            type="button"
                            onClick={() => setDeleteSerialTarget(serial)}
                            className="inline-flex min-h-[36px] items-center rounded-lg border border-red-700/60 px-3 text-xs font-medium text-red-400 hover:bg-red-950/40"
                          >
                            Excluir
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="mt-2 text-xs text-laranjinha/60">
              Cadastrados: {serials.length}/{product.numberOfCards}
            </p>

            {serials.length < product.numberOfCards ? (
              <button
                type="button"
                onClick={() => setSerialCreateOpen(true)}
                className={`mt-2 ${primaryBtnClass}`}
              >
                Cadastrar serial
              </button>
            ) : null}
          </MobileSection>
        </div>
      ) : (
        <p className="px-4 py-6 text-center text-sm text-red-600">Erro ao carregar pedido</p>
      )}

      {products.length > 1 ? (
        <div className="mt-4 flex gap-2 px-4">
          <button
            type="button"
            disabled={currentIndex <= 0}
            onClick={() => setCurrentIndex((c) => c - 1)}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
          >
            Pedido anterior
          </button>
          <button
            type="button"
            disabled={currentIndex >= products.length - 1}
            onClick={() => setCurrentIndex((c) => c + 1)}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
          >
            Próximo pedido
          </button>
        </div>
      ) : null}

      <MobileProductStatusSheet
        open={statusSheetOpen}
        onClose={() => setStatusSheetOpen(false)}
        product={product}
        user={user}
        serials={serials}
        onSuccess={refreshProduct}
        goodToast={goodToast}
        badToast={badToast}
      />

      <MobileProductEditSheet
        open={editSheetOpen}
        onClose={() => setEditSheetOpen(false)}
        product={product}
        user={user}
        onSuccess={refreshProduct}
        goodToast={goodToast}
        badToast={badToast}
      />

      <MobileSerialCreateSheet
        open={serialCreateOpen}
        onClose={() => setSerialCreateOpen(false)}
        product={product}
        user={user}
        onSuccess={() => loadSerials(product?._id)}
        goodToast={goodToast}
        badToast={badToast}
      />

      <MobileSerialEditSheet
        open={serialEditOpen}
        onClose={() => {
          setSerialEditOpen(false);
          setEditingSerial(null);
        }}
        serial={editingSerial}
        product={product}
        user={user}
        onSuccess={() => loadSerials(product?._id)}
        goodToast={goodToast}
        badToast={badToast}
      />

      <MobileConfirm
        open={Boolean(deleteSerialTarget)}
        title="Excluir serial"
        message="Excluir este serial?"
        onConfirm={confirmDeleteSerial}
        onCancel={() => setDeleteSerialTarget(null)}
      />
    </div>
  );
}
